import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSentResetPasswordEmail } from '../hooks/PasswordHooks';
import { toast } from "react-toastify";
import { ClipLoader } from 'react-spinners';
import ValidationSummary from '../ValidationSummary';

/**
 * ForgotPasswordPage component handles the password reset email request flow
 */
const ForgotPasswordPage = () => {
    const { t } = useTranslation();
    const [email, setEmail] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const resetPasswordMutation = useSentResetPasswordEmail();
    const [isSuccess, setIsSuccess] = useState(false);

    /**
     * Validates email format using regex
     * @param {string} email - Email address to validate
     * @returns {boolean} True if email format is valid
     */
    const validateEmail = (email: string) => {
        return email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/);
    };

    /**
     * Handles form submission for password reset request
     * @param {React.MouseEvent<HTMLButtonElement>} e - Click event
     */
    const onSubmit = useCallback<React.MouseEventHandler<HTMLButtonElement>>(
        (e) => {
            e.preventDefault();
            if (isSubmitting) return;
            
            if (!validateEmail(email)) {
                toast.error(t('password.invalidEmail'));
                return;
            }

            setIsSubmitting(true);
            resetPasswordMutation.mutate(email, {
                onError: () => {
                    toast.error(t('password.emailError'));
                    setIsSuccess(false);
                    setIsSubmitting(false);
                },
                onSuccess: () => {
                    toast.success(t('password.emailSent'));
                    setEmail('');
                    setIsSuccess(true);
                    setIsSubmitting(false);
                }
            });
        },
        [email, isSubmitting, resetPasswordMutation, t]
    );

    return (
        <div className="container mt-4">
            {isSuccess ? (
                <div className="alert alert-success">
                    {t('password.emailSent')}
                </div>
            ) : (
                <>
                    {resetPasswordMutation.isError && (
                        <ValidationSummary error={resetPasswordMutation.error} />
                    )}
                    {resetPasswordMutation.isPending ? (
                        <div className="spinner-container" style={{ textAlign: 'center', marginTop: '20px' }}>
                            <ClipLoader size={50} color={"#123abc"} loading={true} />
                            <p>{t('password.sendingEmail')}</p>
                        </div>
                    ) : (
                        <form>
                            <div className="form-group">
                                <label htmlFor="email">{t('password.forgotPasswordEmailAddress')}</label>
                                <input
                                    type="email"
                                    id="email"
                                    className="form-control"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                    disabled={resetPasswordMutation.isPending}
                                />
                            </div>
                            <button
                                className="btn btn-primary mt-3 me-2 mb-2"
                                onClick={onSubmit}
                                disabled={!email || resetPasswordMutation.isPending || isSubmitting}>
                                {t('buttons.send')}
                            </button>
                        </form>
                    )}
                </>
            )}
        </div>
    );
};

export default ForgotPasswordPage;